/**
 * evaluate.ts – Secure code executor for AI-generated JavaScript
 *
 * Executes AI-generated code against a consumer-provided API object within a
 * `with` scope (for ergonomics, not security). Security is provided by the
 * AST validator (validateAiCode) and additional runtime guards.
 *
 * 5 security layers (in execution order):
 *   ① Input validation (type + max length)
 *   ② Rate limiting (per minute)
 *   ③ AST validation (acorn-based, blocks escape vectors)
 *   ④ Execution timeout (Promise.race)
 *   ⑤ Result truncation (max output length)
 */
import { validateAiCode } from "./validateAiCode";
export { roundValues } from "./roundValues";

// ── Configuration ─────────────────────────────────────────────────────────────

export interface EvaluateOptions {
  /** The API object whose methods will be available as globals inside the code. */
  api: Record<string, any>;

  /**
   * Called after successful code execution.
   * Use this to trigger application-specific side effects (e.g. re-render, save undo state).
   */
  onAfterExecute?: () => void;

  /**
   * Called with the raw jsCode string before execution (e.g. for logging).
   */
  onBeforeExecute?: (jsCode: string) => void;

  /**
   * Optional: transform the raw result before it is serialised and returned to the AI.
   * Use this to round numeric values, redact sensitive fields, or reshape the output.
   *
   * @example – round all numbers to 4 decimal places:
   * ```ts
   * transformResult: (result) => roundValues(result, 4)
   * ```
   */
  transformResult?: (result: any) => any;

  /** Max input code length in characters (default: 10000) */
  maxInputLength?: number;
  /** Max result string length in characters (default: 50000) */
  maxResultLength?: number;
  /** Execution timeout in milliseconds (default: 30000) */
  executionTimeoutMs?: number;
  /** Max evaluate calls per rate-limit window (default: 40) */
  rateLimitMaxCalls?: number;
  /** Rate-limit window in milliseconds (default: 60000) */
  rateLimitWindowMs?: number;
}

// ── Rate Limiting ─────────────────────────────────────────────────────────────

let _rateLimitCallCount = 0;
let _rateLimitWindowStart = Date.now();

function checkRateLimit(maxCalls: number, windowMs: number) {
  const now = Date.now();
  if (now - _rateLimitWindowStart > windowMs) {
    _rateLimitCallCount = 0;
    _rateLimitWindowStart = now;
  }
  _rateLimitCallCount++;
  if (_rateLimitCallCount > maxCalls) {
    throw new Error(
      `Rate limit reached: max ${maxCalls} evaluate calls per ${windowMs / 1000}s. Please wait a moment.`
    );
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────────


// ── Main ──────────────────────────────────────────────────────────────────────

/**
 * Creates a configured evaluate function for use as an AI tool.
 *
 * @example
 * ```ts
 * const evaluate = createEvaluate({
 *   api: myEvaluateFunctions,
 *   onAfterExecute: () => { myApp.rerender(); myApp.saveUndo(); },
 * });
 *
 * // In the AI tool definition:
 * const result = await evaluate({ jsCode: "return getAllItems()" });
 * ```
 */
export function createEvaluate(options: EvaluateOptions) {
  const {
    api,
    onAfterExecute,
    onBeforeExecute,
    transformResult,
    maxInputLength = 10_000,
    maxResultLength = 50_000,
    executionTimeoutMs = 30_000,
    rateLimitMaxCalls = 40,
    rateLimitWindowMs = 60_000,
  } = options;

  return async function evaluate({ jsCode }: { jsCode: string }): Promise<string> {
    onBeforeExecute?.(jsCode);

    // ── ① Security: Input validation ──────────────────────────────────────
    if (typeof jsCode !== "string" || jsCode.length > maxInputLength) {
      throw new Error(
        `Code is too long or invalid (${typeof jsCode === "string" ? jsCode.length : typeof jsCode}, max ${maxInputLength} chars). Keep the code short and simple.`
      );
    }

    // ── ② Security: Rate Limiting ─────────────────────────────────────────
    checkRateLimit(rateLimitMaxCalls, rateLimitWindowMs);

    // ── ③ Security: AST validation ────────────────────────────────────────
    const validation = validateAiCode(jsCode);
    if (!validation.safe) {
      const locInfo = validation.loc
        ? ` (line ${validation.loc.line}, col ${validation.loc.column})`
        : "";
      const errorMsg = `Security violation${locInfo}: ${validation.reason}`;
      console.warn("AI code rejected:", errorMsg, "\nCode:", jsCode);
      throw new Error(errorMsg);
    }

    // ── ④ Code execution with timeout ─────────────────────────────────────
    const body = `with (JS_API) { ${jsCode} }`;
    try {
      const AsyncFunction = new Function(
        "return (async function(){}).constructor"
      )() as FunctionConstructor;
      const fn = new AsyncFunction("JS_API", body);

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(
          () =>
            reject(
              new Error(
                `Timeout: code execution exceeded the ${executionTimeoutMs / 1000}s limit.`
              )
            ),
          executionTimeoutMs
        );
      });

      // ── Async-call tracking ──────────────────────────────────────────────
      // If the AI code calls an async function WITHOUT await (e.g. setUISelectValue(...)),
      // the returned Promise runs in the background and any rejection becomes an
      // unhandled promise rejection that bypasses the AI error channel.
      //
      // Solution: wrap every API function so that any returned Promise is silently
      // captured. After the main code returns we await all captured promises and
      // re-throw the first error – so the AI always receives the error.
      const pendingErrors: Error[] = [];
      const pendingSettles: Promise<void>[] = [];

      // Use a Proxy so `this` inside API methods still points to the proxy,
      // ensuring that internal `this.xxx()` calls are also tracked.
      const trackedApi: typeof api = new Proxy(api as any, {
        get(target, prop) {
          const value = (target as any)[prop];
          if (typeof value !== "function") return value;
          return function (...args: any[]) {
            // Call with trackedApi as `this` so nested this.xxx() calls are tracked too
            const result = (value as Function).apply(trackedApi, args);
            if (result instanceof Promise) {
              pendingSettles.push(
                result.catch((e: unknown) => {
                  pendingErrors.push(e instanceof Error ? e : new Error(String(e)));
                })
              );
            }
            return result;
          };
        },
      });

      let result = await Promise.race([fn(trackedApi), timeoutPromise]);

      if (result instanceof Promise) {
        result = await result;
      }

      // Application-specific side effects BEFORE waiting for pending async calls.
      // This is important: onAfterExecute typically triggers a UI re-render
      // (e.g. APP.arrangement.update3d()). Pending async calls like setUISelectValue
      // may be waiting for DOM elements that only appear after that re-render.
      // Calling onAfterExecute first avoids a circular wait / timeout.
      onAfterExecute?.();

      // Wait for any non-awaited async API calls to settle, then surface errors
      if (pendingSettles.length > 0) {
        await Promise.all(pendingSettles);
      }
      if (pendingErrors.length > 0) {
        throw pendingErrors[0];
      }
      if (result == undefined) {
        result = { success: true };
      }

      if (transformResult) {
        result = transformResult(result);
      }

      if (
        !(
          typeof result === "string" ||
          typeof result === "boolean" ||
          typeof result === "number"
        )
      ) {
        result = JSON.stringify(result, (key, value) => {
          if (value?.toJSON) {
            return value.toJSON();
          }
          return value;
        });
      }


      // ── ⑤ Security: Result truncation ─────────────────────────────────
      if (typeof result === "string" && result.length > maxResultLength) {
        result =
          result.substring(0, maxResultLength) +
          `… [truncated, ${result.length} → ${maxResultLength} chars]`;
      }

      console.log("evaluate result", result);
      return typeof result === "string" ? result : String(result);
    } catch (error) {
      console.error("evaluate error", error);
      throw error;
    }
  };
}

